<script>
// ★ [spec-002 T050/T051/T052 · US-5/US-7 · IA-001 D5/D6] Cartão de fonte da WEB.
//
// **O defeito que este componente fecha:** o fio inteiro já existia e NINGUÉM
// desenhava. O evento `busca` chega ao front (`api/chat-event-handlers.js:158`),
// é guardado (`store/mutations.js::ADICIONAR_BUSCA_STREAMING`) e sai por um
// getter (`store/getters.js::buscaStreaming`) — e `grep -rn buscaStreaming
// --include='*.vue'` devolvia **0**. O consultor recebia a resposta e não tinha
// como saber de onde o número externo veio.
//
// **Por que um componente próprio e não mais uma seção em `chat-message-fontes`**
// (IA-001 **D5**): a linha "Fontes:" é RESERVADA às fontes INTERNAS (tabela do
// escritório). Misturar URL de fora nela apagaria exatamente a distinção que a
// taxonomia de origem do dado existe para manter — o consultor precisa saber se
// o número saiu da base dele ou da internet. A separação é de COMPONENTE, não de
// filtro no mesmo lugar (ver o comentário gêmeo em `chat-message-fontes.vue`).
//
// **Do PAYLOAD, nunca do texto** (T051): as fontes vêm de `evento.fontes`, que o
// worker monta a partir do `tool_result` real (`agent_sdk/stream.py::
// _fontes_de_busca_harness`). Ler `<a>` da prosa seria impossível de qualquer
// forma — QUATRO personas têm `neutralizaAncoras: true` e apagam todo link da
// saída (`constants/Personas.js:110,159,180,205`).
import { aawzDate } from '@utils/dates/aawz-date/index'
import { getPersona } from '@modules/aawz-intelligence-v2/constants/Personas'

const STORE_MODULE = 'aawz-intelligence-v2'

// Quantas fontes ficam visíveis antes do "ver mais". Três é o que cabe na bolha
// sem empurrar a resposta para fora da dobra; o resto abre por clique.
//
// ⚠️ NÃO é uma seção colapsável nascendo fechada (regra do DS que
// `chat-message-plano` cita): a seção NASCE ABERTA com as três primeiras — o que
// colapsa é a CAUDA da lista, que é outra coisa.
const FONTES_VISIVEIS = 3

const FORMATO_DA_DATA = 'DD/MM/YYYY HH:mm'

// ★ [MUST-3 da review do PR #2328] Allowlist de ESQUEMA do `href`.
//
// O que chega aqui é payload da busca WEB — conteúdo EXTERNO, que nenhuma
// camada anterior valida: o worker faz só `str(item.get('url'))[:2048]`
// (`agent_sdk/stream.py`) e `_fontes_web_apoio.py` repassa o item cru. Vue 2
// NÃO sanitiza URL em `:href` (a proteção existe para `v-html`, não para
// atributo), então um `javascript:` no payload vira execução de script no
// origin autenticado, multi-tenant, a um clique do consultor.
//
// Fonte fora da allowlist é DESCARTADA, não rebaixada a texto: é o mesmo
// tratamento da fonte sem URL logo abaixo — um endereço que o consultor não
// consegue abrir e conferir não atesta procedência nenhuma, e imprimir
// `javascript:alert(1)` como "domínio" seria lixo na tela.
const ESQUEMA_PERMITIDO = /^https?:\/\//i

// ★ [MUST-1 da review do PR #2355] O rótulo é SEMPRE o host do `:href`.
//
// **O invariante: o domínio exibido é o host da URL que o link abre.** Antes
// desta review o `dominio` do PAYLOAD vencia o host real — num cartão cuja
// função é atestar PROCEDÊNCIA, isso é spoofing: a linha podia dizer
// `www.bcb.gov.br` e o clique ir para `evil.com`. O payload da busca é
// conteúdo EXTERNO e não é fonte de verdade sobre si mesmo; por isso
// `fonte.dominio` não é lido em lugar nenhum, **nem como fallback**.
//
// Derivar do href fecha a CLASSE inteira em vez das variantes conhecidas — o
// que vale mais que a lista abaixo, que é só o porquê de cada corte.

// Tab, CR e LF são REMOVIDOS da URL pelo browser antes de ela ser resolvida
// (WHATWG URL, "URL parsing"): mantê-los faria o rótulo divergir do destino
// real por um caractere que ninguém vê na tela.
const CARACTERES_QUE_O_BROWSER_REMOVE = /[\t\n\r]/g

// O host termina no PRIMEIRO `/`, `\`, `?` ou `#`. A barra invertida entra
// porque o browser a trata como `/` em esquema especial (http/https), e o `#`
// porque `https://evil.com#@www.bcb.gov.br` é fragmento, não host — os dois
// enganavam o corte antigo, que só olhava `/` e `?`.
const FIM_DA_AUTHORITY = /[/\\?#]/

/**
 * Host de destino de uma URL — o que o browser de fato abre no clique.
 *
 * Sem depender de `URL` (jsdom/Node antigos divergem no tratamento de
 * endereço inválido: um lança, o outro devolve string vazia).
 *
 * @param {String} url endereço cru do payload, do jeito que vai para o `href`.
 * @returns {String} host de destino, ou string vazia quando não dá para extrair.
 */
function hostDaUrl(url) {
  if (typeof url !== 'string') return ''
  const limpa = url.replace(CARACTERES_QUE_O_BROWSER_REMOVE, '')
  const semEsquema = limpa.replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')
  const fimDaAuthority = semEsquema.search(FIM_DA_AUTHORITY)
  const authority =
    fimDaAuthority === -1 ? semEsquema : semEsquema.slice(0, fimDaAuthority)
  // Userinfo é tudo que vem antes do ÚLTIMO `@`: em `a@b@evil.com` quem manda
  // no destino é `evil.com`.
  return authority.split('@').pop()
}

export default {
  name: 'IntelligenceV2ChatMessageFontesWeb',
  props: {
    // Persona da SUPERFÍCIE (D6, T052). `null` cai na default — nunca em
    // `undefined.mostraFontes`, que seria falsy em silêncio (é o mesmo motivo do
    // `Object.hasOwn` em `getPersona`).
    persona: {
      type: Object,
      default: null,
    },
    // [T051b, spec-002 US-5] `message.fontes_web` — a lista CRUA que o
    // backend grava nos DOIS gravadores da mensagem do assistente
    // (`_fontes_web_apoio.fontes_web_dos_eventos_busca`), sem embrulho de
    // evento: cada item já é a citação, com o próprio `coletado_em`.
    //
    // Presente (não vazia) ⇒ mensagem PERSISTIDA (histórico/reload) — é ELA
    // que sobrevive ao F5, e por isso VENCE o canal transiente. Ausente/
    // vazia ⇒ sem dado persistido ainda (turno em streaming, antes do
    // `end`, ou mensagem sem busca nenhuma) — o componente cai para
    // `buscaStreaming`, como já fazia antes desta task.
    fontesPersistidas: {
      type: Array,
      default: null,
    },
    // [MUST-2 da review do PR #2328] `buscaStreaming` é canal por TURNO
    // (zerado em `resetChatState`, a cada turno novo — ver `store/actions.js`)
    // mas este componente é montado em CADA bolha de assistente da conversa.
    // Sem escopo, durante e depois de um turno com busca, TODAS as respostas
    // ANTERIORES da mesma conversa (sem `fontesPersistidas` própria) liam o
    // MESMO canal e mostravam a fonte de OUTRO turno — um componente cuja
    // única função é atestar procedência mostrando procedência errada.
    //
    // `aoVivo` é a vivacidade da BOLHA (o mesmo `isStreaming` que
    // `chat-message.vue` já calcula): só ELA pode cair para o canal
    // transiente quando `fontesPersistidas` está vazia. Default `false` —
    // FAIL-CLOSED: bolha que não declara vivacidade explicitamente não lê o
    // turno em voo (é o comportamento seguro; deixar de passar a prop nunca
    // deve vazar dado de outro turno).
    aoVivo: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expandido: false,
    }
  },
  computed: {
    // Leitura direta do store, sem `mapGetters` — mesmo padrão de
    // `chat-indice-artefatos.vue`: os units montam com um `$store` plano.
    //
    // [MUST-2] Só a bolha VIVA (`aoVivo`) lê o canal transiente — é ele quem
    // desenha DURANTE o turno, antes de a mensagem existir para gravar
    // `fontes_web`. Uma bolha que não é a viva (histórico, ou a viva de um
    // turno anterior já encerrado) nunca cai aqui: sem isso ela leria o
    // turno de OUTRA resposta, que é justamente o MUST-2.
    eventosDeBusca() {
      if (!this.$store || !this.aoVivo) return []
      return this.$store.getters[STORE_MODULE + '/buscaStreaming'] || []
    },
    personaEfetiva() {
      return this.persona || getPersona(null)
    },
    // D6/T052: superfície que não exibe fontes não exibe fonte NENHUMA — nem a
    // interna (regra já vigente em `chat-message.vue:553`) nem a da web.
    superficieExibeFontes() {
      return Boolean(this.personaEfetiva.mostraFontes)
    },
    // [T051b] Citações CRUAS, de UMA das duas fontes — nunca as duas
    // somadas (dobraria o cartão na virada do turno para a mensagem
    // persistida). `fontesPersistidas` VENCE quando não-vazia: é o dado que
    // sobrevive ao F5, e o canal `buscaStreaming` é transiente por
    // desenho. Cada item já carrega o próprio `coletado_em` — diferente do
    // shape do evento de streaming, que carimba a data no NÍVEL DO EVENTO
    // (ver `dataDeColeta`).
    citacoesBrutas() {
      if (Array.isArray(this.fontesPersistidas) && this.fontesPersistidas.length > 0) {
        return this.fontesPersistidas.map((fonte) => ({
          fonte: fonte,
          coletadoEmBruto: fonte && fonte.coletado_em,
        }))
      }
      const lista = []
      this.eventosDeBusca.forEach((evento) => {
        const fontesDoEvento = (evento && evento.fontes) || []
        fontesDoEvento.forEach((fonte) => {
          lista.push({ fonte: fonte, coletadoEmBruto: evento && evento.coletado_em })
        })
      })
      return lista
    },
    // Uma fonte por URL, na ordem em que apareceu. A PRIMEIRA ocorrência vence:
    // é a que o consultor viu aparecer, e trocar o título depois faria o cartão
    // mudar debaixo do olho dele.
    fontes() {
      if (!this.superficieExibeFontes) return []
      const vistas = {}
      const lista = []
      this.citacoesBrutas.forEach(({ fonte, coletadoEmBruto }) => {
        const url = fonte && typeof fonte.url === 'string' ? fonte.url.trim() : ''
        // Cartão sem endereço não é fonte: seria uma afirmação de procedência
        // que o consultor não consegue conferir.
        if (!url || vistas[url]) return
        // [MUST-3] Esquema fora de http/https não chega ao `:href` — nem no
        // streaming nem no persistido, porque os dois carregam o MESMO payload
        // externo (um ao vivo, outro já gravado na coluna `fontes_web`).
        if (!ESQUEMA_PERMITIDO.test(url)) return
        vistas[url] = true
        // [MUST-1] Derivado do `href` que ESTE cartão renderiza — nunca do
        // `fonte.dominio`, que é só mais um campo do mesmo payload externo (o
        // worker o monta com `urlparse(url).netloc`, que preserva o userinfo).
        const dominio = hostDaUrl(url)
        lista.push({
          url: url,
          // Título ausente cai para o domínio — nunca em branco.
          titulo: fonte.titulo || dominio || url,
          dominio: dominio,
          coletadoEm: this.dataDeColeta(coletadoEmBruto),
        })
      })
      return lista
    },
    fontesExibidas() {
      return this.expandido ? this.fontes : this.fontes.slice(0, FONTES_VISIVEIS)
    },
    restantes() {
      return Math.max(0, this.fontes.length - FONTES_VISIVEIS)
    },
    mostrarVerMais() {
      return !this.expandido && this.restantes > 0
    },
    mostrar() {
      return this.fontes.length > 0
    },
  },
  methods: {
    /**
     * Data de coleta exibida no cartão.
     *
     * ⚠️ No streaming, `coletado_em` é carimbado pelo STORE no instante em que
     * o evento chega (`ADICIONAR_BUSCA_STREAMING`), porque o payload `d_busca`
     * do worker não tem campo de data — o envelope tem `timestamp_worker`, mas
     * ele é desembrulhado antes de chegar aqui (`api/chat-event-handlers.js`).
     * Isso é "quando esta tela recebeu", e não "quando o provedor indexou a
     * página": a diferença é de segundos, mas ela existe e está escrita aqui
     * para ninguém ler o campo como data do CONTEÚDO. [T051b] Na mensagem
     * PERSISTIDA o valor vem do PRÓPRIO item (`fonte.coletado_em`, gravado
     * pelo backend) — mesmo campo, fonte diferente; por isso este método
     * recebe o valor já resolvido por `citacoesBrutas`, não o evento inteiro.
     *
     * @param {String} bruto `coletado_em` já resolvido (evento ou citação).
     * @returns {String} data formatada, ou string vazia quando não há carimbo.
     */
    dataDeColeta(bruto) {
      if (!bruto) return ''
      return aawzDate(bruto).format(FORMATO_DA_DATA)
    },
    expandir() {
      this.expandido = true
    },
  },
}
</script>

<template>
  <div v-if="mostrar" class="chat-message-fontes-web mb-4">
    <div class="chat-message-fontes-web__titulo az-text-secondary az-size-12 mb-2">
      Fontes da internet consultadas nesta resposta
    </div>
    <a
      v-for="fonte in fontesExibidas"
      :key="fonte.url"
      :href="fonte.url"
      target="_blank"
      rel="noopener noreferrer"
      class="chat-message-fontes-web__cartao d-flex align-center az-gap-small px-3 py-2 mb-2"
      data-test="fonte-web-cartao"
    >
      <icon-svg
        name="open-external"
        :size="14"
        color="var(--neutral-600)"
        class="chat-message-fontes-web__icone flex-shrink-0"
      />
      <span class="chat-message-fontes-web__texto d-flex flex-column">
        <span
          class="chat-message-fontes-web__nome az-text-primary az-size-14 font-weight-medium"
          data-test="fonte-web-titulo"
        >{{ fonte.titulo }}</span>
        <span class="az-text-secondary az-size-12 az-muted">
          <span data-test="fonte-web-dominio">{{ fonte.dominio }}</span>
          <span v-if="fonte.coletadoEm" data-test="fonte-web-data"> · coletada em {{ fonte.coletadoEm }}</span>
        </span>
      </span>
    </a>
    <button
      v-if="mostrarVerMais"
      class="chat-message-fontes-web__ver-mais az-text-secondary az-size-12 text-left"
      data-test="fonte-web-ver-mais"
      @click="expandir"
    >
      Ver mais {{ restantes }} fontes
    </button>
  </div>
</template>

<style lang="sass" scoped>
.chat-message-fontes-web
  &__cartao
    // `display: flex` fica só na classe `d-flex` do template — declará-lo de
    // novo aqui duplicaria a utility do Vuetify (regra do design-system).
    border: 1px solid var(--neutral-300)
    border-radius: var(--radius-md)
    background: var(--white)
    text-decoration: none
    transition: border-color .15s ease

    &:hover
      border-color: var(--primary)

  &__nome
    word-break: break-word

  &__ver-mais
    background: none
    border: none
    padding: 0
    cursor: pointer
    text-decoration: underline
</style>
